import type {
  BoxCapacity,
  BoxCode,
  CompatibilityGroup,
  Variety,
} from "@/lib/catalog/types";

export type CartLine = {
  slug: string;
  variety: string;
  category: Variety["category"];
  // length is optional because hydrangeas have no length concept.
  length?: number;
  // #63 box_stem: "stem" = loose stems of a box_stem variety (hydrangea). For a
  // stem line `quantity` is the STEM count and `stems === quantity`
  // (stemsPerBunch stays null). The packer drains stems into EB/QB boxes sized
  // by the variety's grade (unitsPerBox). "bunch"/"box" behavior is unchanged.
  unit: "bunch" | "box" | "stem";
  quantity: number;
  stems: number;
  pricePerStem: number;
  stemsPerBunch: number | null;
  // Step 6.5: when the variety has colorOptions, the user's choice is
  // recorded here. Special value "mix" means "Assorted - farm's seasonal
  // pick", which the fulfillment side will assemble.
  colorChoice?: string;
  // DIRECTIVE #022: ISO timestamp used by V3 bucket packer FIFO drain.
  // Optional for back-compat with old serialized cart state - V3 code
  // falls back to "now" when reading and always populates on write.
  addedAt?: string;
  // DIRECTIVE #024: explicit box size the buyer picked (or default-
  // resolved). The V3 bucket packer's hydrangea/box-unit path reads
  // this to honor the buyer's choice instead of forcing the smallest
  // available size. For bunch-unit lines this is informational only
  // (stems are derived from quantity * stemsPerBunch).
  boxCode?: BoxCode;
  // #468-ENFORCEMENT A1: per-box unit-count capacity ({EB,QB,HB}) for THIS
  // line's variety at THIS line's length (bunches for box_bunch, stems for
  // box_stem). DERIVED at pack time from the live variety's unitsPerBox - never
  // persisted (materializeCart re-stamps it each replay from DDB). computeBoxFill
  // reads unitsPerBox[box.size] as the fractional denominator (one unit = 1/that
  // of a box). Absent on old in-flight lines -> computeBoxFill falls back to the
  // derived floor(capacity.stems / stemsPerBunch) target.
  unitsPerBox?: BoxCapacity;
  // #468-ENFORCEMENT A1: the LIVE mix group (mixGroupOf) stamped at pack time,
  // so bucketing reads Alexis's merged groups from DDB, not the stale static
  // seed. null = box_only (never mixes). Derived, never persisted.
  mixGroup?: number | null;
  // #468 SIZE DRIVES PACKING: the client-chosen box SIZE for this line's GROUP
  // (Small/Standard/Large = EB/QB/HB). Unlike the retired display-only
  // selectedSize, this is a PACKER INPUT: the packer fills the group's boxes to
  // THIS size (consolidating or splitting the group), and it rides the item wire
  // (cartToServerItems -> ServerCartItem.groupBoxSize) so the client cart, the
  // invoice, and freight all pack identically (#384 parity). Unset -> the packer
  // uses its default (QB / HB-threshold). All bunch lines in one group share it.
  groupBoxSize?: BoxCode;
};

export type CartBoxCapacity = {
  stems: number;
  bunches?: number;
};

export type CartBox = {
  id: string;
  compatibilityGroup: CompatibilityGroup;
  size: BoxCode;
  capacity: CartBoxCapacity;
  contents: CartLine[];
  // #468 fill-communication: the box's client-SELECTED target size (EB/QB/HB).
  // Transient UI state re-applied after each re-materialize from a persisted
  // anchor->size map (like #431's stable order); NOT persisted on the server
  // cart and never seen by the packer/invoice. When unset, computeBoxFill
  // targets the packer's own `size`. The box is "complete" at this size.
  selectedSize?: BoxCode;
};

export type Cart = {
  boxes: CartBox[];
  subtotal: number;
  meetsMinimum: boolean;
};

export type CartAddInput = {
  variety: Variety;
  length?: number;
  // #63 box_stem: "stem" adds loose stems (quantity = stem count) of a
  // box_stem variety. See CartLine.unit.
  unit: "bunch" | "box" | "stem";
  quantity: number;
  // Step 6.5: optional color selection (slug or "mix"). When set and the
  // variety has priceByColor[colorChoice], that price overrides the
  // pricePerStem lookup.
  colorChoice?: string;
  // DIRECTIVE #023: explicit box size selection (e.g., "EB" or "QB").
  // When unit==="box" and boxCode is set, stems = quantity * stemsInBox(
  // cap, boxCode). When omitted, falls back to smallestAvailableBox(cap)
  // (legacy behavior). Set by the UI after getDefaultBoxUnit() resolves
  // a saved-or-universal-QB default.
  boxCode?: BoxCode;
  // #468 STABLE ORDER: the ORIGINAL add time, threaded through the
  // materialize replay so a re-pack preserves each line's add time (and thus
  // its box's stable position). A genuine new add omits it -> stamped now.
  addedAt?: string;
  // #468 SIZE DRIVES PACKING: the group's chosen box size, carried through the
  // materialize replay so the packer fills to it. A genuine new add inherits its
  // target group's current choice (addToBox), else undefined -> packer default.
  groupBoxSize?: BoxCode;
};

// #430: the "mix-conflict" outcome was removed with the Rose+Garden cross-mix
// (#353/#429). Adds always succeed into the correct bucket.
export type CartAddOutcome = { kind: "added"; cart: Cart; boxId: string };

export const EMPTY_CART: Cart = {
  boxes: [],
  subtotal: 0,
  meetsMinimum: false,
};
